import React, { useState } from "react";
import axios from "axios";
import { useI18n } from "../i18n/I18nProvider";
import { LogoLockup } from "./Logo";
import LanguageSelector from "./LanguageSelector";
import { FOOTER, NEWSLETTER } from "../constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Footer = () => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/newsletter`, { email });
      setStatus("success");
      setEmail("");
    } catch (_) {
      setStatus("error");
    }
  };

  const navItems = [
    { id: "capabilities", label: t.nav.capabilities },
    { id: "technology", label: t.nav.technology },
    { id: "showcase", label: t.nav.showcase },
    { id: "philosophy", label: t.nav.philosophy },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <footer
      data-testid={FOOTER.root}
      className="relative bg-black border-t border-white/8 px-6 md:px-10 lg:px-14 pt-24 md:pt-32 pb-12"
    >
      <div className="mx-auto max-w-[1440px]">
        {/* Newsletter row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 pb-16 border-b border-white/8">
          <div>
            <h3 className="font-display text-white text-[28px] md:text-[36px] lg:text-[44px] leading-[1.05] tracking-tight max-w-[18ch]">
              {t.newsletter.title}
            </h3>
          </div>
          <form
            data-testid={NEWSLETTER.form}
            onSubmit={subscribe}
            className="flex flex-col gap-4 self-end w-full max-w-md justify-self-start lg:justify-self-end"
          >
            <div className="flex items-end gap-4 border-b border-white/20 focus-within:border-white/80 transition-colors pb-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                data-testid={NEWSLETTER.email}
                className="flex-1 bg-transparent text-white placeholder:text-white/35 py-2 outline-none text-[15px]"
              />
              <button
                type="submit"
                data-testid={NEWSLETTER.submit}
                disabled={status === "sending"}
                className="text-[11px] tracking-[0.22em] uppercase font-mono text-white hover:text-white/70 transition-colors py-2"
              >
                {status === "sending" ? t.newsletter.submitting : t.newsletter.submit} →
              </button>
            </div>
            {status === "success" && (
              <span data-testid={NEWSLETTER.success} className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/70">
                ✓ {t.newsletter.success}
              </span>
            )}
            {status === "error" && (
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/55">
                {t.newsletter.error}
              </span>
            )}
          </form>
        </div>

        {/* Main columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 py-16">
          <div className="md:col-span-6">
            <LogoLockup height={56} />
            <p className="mt-8 text-white/55 text-[14px] leading-[1.65] max-w-md">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/45 mb-5">
              {t.footer.nav}
            </div>
            <ul className="flex flex-col gap-3">
              {navItems.map((it) => (
                <li key={it.id}>
                  <button
                    onClick={() => scrollToId(it.id)}
                    className="text-white/75 hover:text-white text-[14px] tracking-tight transition-colors"
                  >
                    {it.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/45 mb-5">
              {t.footer.language}
            </div>
            <LanguageSelector scope="footer" />
          </div>
        </div>

        {/* Legal / Company info */}
        <div className="py-10 border-t border-white/8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 text-[12px] leading-[1.75] text-white/50">
          <div>
            <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/45 mb-3">
              {t.footer.company}
            </div>
            <p>
              Gaelon Tecnologia e Negócios Digitais LTDA
              <br />
              <span className="font-mono text-[11px] tracking-[0.08em] text-white/45">
                CNPJ 68.330.345/0001-47
              </span>
            </p>
          </div>

          <div>
            <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/45 mb-3">
              {t.footer.contact}
            </div>
            <ul className="flex flex-col gap-1.5">
              <li>
                <a
                  data-testid="footer-contact-email"
                  href="mailto:contato@gaelon.com.br"
                  className="hover:text-white transition-colors"
                >
                  contato@gaelon.com.br
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-sac-email"
                  href="mailto:sac@gaelon.com.br"
                  className="hover:text-white transition-colors"
                >
                  sac@gaelon.com.br
                </a>
                <span className="ml-2 font-mono text-[10px] tracking-[0.24em] uppercase text-white/35">
                  {t.footer.sac}
                </span>
              </li>
              <li>
                <a
                  data-testid="footer-phone"
                  href="https://wa.me/5511914479621"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  +55 11 91447-9621
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-10 border-t border-white/8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <span
            data-testid={FOOTER.copyright}
            className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40"
          >
            GAELON © 2026 — {t.footer.rights}
          </span>
          <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/40">
            INTELLIGENCE / INFRASTRUCTURE / NEXT ERA
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
